/**
 * Step 2 of the village analyser: where the client stands STAYING PUT, as four numbers, with the
 * contract beside them and how long the money lasts beneath.
 *
 * WHY STAYING PUT (owner, 24 Sep 2026). A village move is a choice, so the only honest benchmark is
 * life as it is. The plan summary used to head "No plan yet" over figures for a plan the app had
 * chosen on the client's behalf — the move, with the entry contribution taken from savings in a
 * default order — while the Suggestion Engine measured everything against staying put. Two
 * baselines in one module. This step IS the one baseline: every strategy in step 3 is measured
 * against these numbers, in both modes.
 *
 * ONE SOURCE. The pension and cashflow are the standalone annual assessment of the profile as it
 * stands (`initialFinancials`, the same figures the rail's "before" column reads); the estate and
 * the runway come from the stay-put projection — the village zeroed out — which is the same run the
 * Suggestion Engine's own baseline candidate makes.
 *
 * Sibling of Aged Care's WalkInHero: three cards of one width, the numbers first, the words after.
 */
import React from 'react';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { ArrowRight, ChevronLeft, Eye } from 'lucide-react';
import { FundingRunway } from '../../components/narrative/FundingRunway.tsx';
import type { PlanRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { GradientText } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { RATES_PROVENANCE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { VillageInputs } from './RetirementVillagePage.tsx';
import { DEPARTURE_LADDER_YEARS, exitAfter } from './RvVillageCostsTile.tsx';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { MeansTestBeats } from '../../components/insights/MeansTestPosition.tsx';

interface RvStayPutHeroProps {
    factFind: DraftFactFind;
    /** Today's standalone assessment — pension, cashflow, net worth. Null while it loads. */
    todayFinancials: { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null;
    /** The estate at the horizon, staying put. Null until the stay-put projection has run. */
    stayPutLegacy: number | null;
    /** How long the money lasts, staying put — struck on the stay-put projection. */
    runway: PlanRunwayCopy | null;
    legacyYears: number;
    villageInputs: VillageInputs;
    annualOngoing: number;
    /** The tile's own "after N years" is editable (owner, 24 Sep 2026). */
    onLegacyYearsChange?: (years: number) => void;
    onBack: () => void;
    onNext: () => void;
    onAssumptions: () => void;
    /** Opens the projections for staying put over the legacy horizon. */
    onShowProjections: () => void;
}

const CARD = 'rounded-2xl border border-white/80 bg-white/60 p-6 shadow-lg shadow-gray-200/50 backdrop-blur-xl';

const Tile = ({ label, value, unit, caption, tone }: { label: string; value: string; unit?: string; caption: React.ReactNode; tone: 'cost' | 'good' | 'bad' | 'family' }) => {
    const rule = { cost: 'bg-slate-500', good: 'bg-emerald-400', bad: 'bg-rose-400', family: 'bg-[#0290F5]' }[tone];
    const figure = { cost: 'text-slate-900', good: 'text-emerald-700', bad: 'text-rose-700', family: 'text-slate-900' }[tone];
    return (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 pb-4 pt-5 shadow-card">
            <span className={`absolute inset-x-0 top-0 h-1 ${rule}`} aria-hidden />
            <p className="text-[13px] font-semibold text-slate-600">{label}</p>
            <div className="mt-1.5">
                <span className={`text-[28px] font-bold leading-none tabular-nums tracking-tight ${figure}`}>
                    {value}
                    {unit && <span className="ml-1 text-[13px] font-semibold text-slate-500">{unit}</span>}
                </span>
            </div>
            <p className="mt-2 text-[13px] leading-snug text-slate-600">{caption}</p>
        </div>
    );
};

const RvStayPutHero = ({
    factFind,
    todayFinancials,
    stayPutLegacy,
    runway,
    legacyYears,
    villageInputs,
    annualOngoing,
    onLegacyYearsChange,
    onBack,
    onNext,
    onAssumptions,
    onShowProjections,
}: RvStayPutHeroProps) => {
    const name = factFind?.client?.firstName || 'you';
    const isCouple = factFind?.adviceFor === 'couple' && !!factFind?.partner?.firstName;
    const household = isCouple ? `${name} & ${factFind.partner!.firstName}` : name;
    const ready = !!todayFinancials;
    const pension = todayFinancials?.socialSecurity ?? 0;
    const cashFlow = todayFinancials?.cashFlow ?? 0;
    const netWorth = todayFinancials?.netWorth ?? 0;
    const years = Number(legacyYears) || 0;
    const entryFee = villageInputs.entryFee || 0;
    const forLife = !(villageInputs.yearsResidency > 0);

    return (
        <div className="space-y-6">
            {/* 1. The four numbers, staying put */}
            <div className={CARD}>
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Step 2 of 3</p>
                <h3 className="mt-1 text-[22px] font-bold leading-tight tracking-tight">
                    <GradientText>Where {household} {isCouple ? 'stand' : 'stands'} today, with no move.</GradientText>
                </h3>
                <p className="mt-1 text-[14px] text-slate-600">
                    Life as it is. This is <span className="font-semibold text-slate-800">staying put</span>: the baseline every
                    way of funding the move in step 3 is measured against.
                </p>
                {!ready ? (
                    <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-6 text-center text-[14px] text-slate-600">
                        Working out today&apos;s position…
                    </div>
                ) : (
                    <>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            <Tile label="Age Pension today" value={formatCurrency(Math.round(pension))} unit="a year" caption={pension > 0 ? 'Estimated entitlement on today’s assets and income.' : 'No entitlement on today’s assets and income.'} tone="good" />
                            <Tile
                                label={cashFlow < 0 ? 'Left to find each year' : 'Left over each year'}
                                value={`${cashFlow < 0 ? '−' : '+'}${formatCurrency(Math.abs(Math.round(cashFlow)))}`}
                                caption={cashFlow < 0 ? 'Money in, less money out. Met from savings.' : 'Money in, less money out, before any village fees.'}
                                tone={cashFlow < 0 ? 'bad' : 'good'}
                            />
                            <Tile label="Net worth today" value={formatCurrency(Math.round(netWorth))} caption="Everything owned, less everything owed." tone="cost" />
                            <Tile
                                label="What's left to the family"
                                value={stayPutLegacy != null ? formatCurrency(Math.round(stayPutLegacy)) : '—'}
                                caption={stayPutLegacy != null ? (
                                    onLegacyYearsChange ? (
                                        <ProjectionYearsInput years={years} onChange={onLegacyYearsChange} after=" staying put." />
                                    ) : `After ${years} ${years === 1 ? 'year' : 'years'} staying put.`
                                ) : 'Not available: the stay-put projection could not be run.'}
                                tone="family"
                            />
                        </div>
                        <p className="mt-3 text-[13px] text-slate-500">
                            Figures use the rates in force from {RATES_PROVENANCE.agePension}. The{' '}
                            <button type="button" onClick={onAssumptions} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                                Assumptions
                            </button>{' '}
                            page lists every one, with its source and date.
                        </p>
                    </>
                )}
            </div>

            {/* The means test, in words: which test sets the pension and what would have to move
                (owner, 24 Sep 2026). The same two beats every module carries. */}
            {ready && (
                <div className={CARD}>
                    <PlainWordsCard eyebrow={`Where ${household} ${isCouple ? 'stand' : 'stands'} today`} className="-mt-5 -mx-5 border-0 bg-transparent shadow-none" collapsible>
                        <MeansTestBeats careDepositRelevant={Boolean(factFind?.goals?.aged_care)} financials={todayFinancials} isCouple={factFind?.adviceFor === 'couple'} name={factFind?.client?.firstName || 'you'} alsoMoves={<>changes what the village move costs the pension</>} />
                    </PlainWordsCard>
                </div>
            )}

            {/* 2. The contract, beside the position but not in it */}
            {entryFee > 0 && (
                <div className={CARD}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400">What the village would cost</p>
                    <p className="mt-1 text-[14px] text-slate-600">
                        None of this is in the figures above. The move — and how the entry contribution is paid — is what step 3 models.
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
                            <p className="text-[13px] font-semibold text-slate-600">Entry payment</p>
                            <p className="mt-1 text-[22px] font-bold tabular-nums text-slate-900">{formatCurrency(entryFee)}</p>
                            <p className="mt-1 text-[13px] text-slate-500">Paid once, on moving in. Refundable on leaving, less the departure fee.</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
                            <p className="text-[13px] font-semibold text-slate-600">Ongoing fees</p>
                            <p className="mt-1 text-[22px] font-bold tabular-nums text-slate-900">{formatCurrency(annualOngoing)}<span className="ml-1 text-[13px] font-semibold text-slate-500">a year</span></p>
                            <p className="mt-1 text-[13px] text-slate-500">Services, maintenance and shared facilities, from the day of the move.</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
                            <p className="text-[13px] font-semibold text-slate-600">{forLife ? 'If you left the village' : `You get back after ${Math.floor(villageInputs.yearsResidency)} years (est.)`}</p>
                            {forLife ? (
                                <div className="mt-1.5 space-y-1">
                                    {DEPARTURE_LADDER_YEARS.map((y) => {
                                        const e = exitAfter(villageInputs, y);
                                        return (
                                            <div key={y} className="flex items-baseline justify-between gap-2 text-[13px]">
                                                <span className="text-slate-500">after {y} years</span>
                                                <span className="tabular-nums"><span className="text-slate-400">fee {formatCurrency(e.dmf)} · </span><span className="font-semibold text-slate-900">{formatCurrency(e.netToResident)}</span></span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="mt-1 text-[22px] font-bold tabular-nums text-slate-900">{formatCurrency(exitAfter(villageInputs, Math.floor(villageInputs.yearsResidency)).netToResident)}</p>
                            )}
                            <p className="mt-1 text-[13px] text-slate-500">{forLife ? 'The departure fee grows with the stay. Modelled as staying for life.' : 'After the departure fee. The projection sells the unit in that year.'}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. How long the money lasts, staying put */}
            {ready && runway && (
                <div className={CARD}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400">How long the money lasts, staying put</p>
                    <div className="mt-2">
                        <FundingRunway
                            headline={<span className="text-[18px] font-bold leading-tight tracking-tight text-slate-900">{runway.headline}</span>}
                            coveredPct={runway.coveredPct}
                            leftLabel={runway.leftLabel}
                            rightLabel={runway.rightLabel}
                            depleted={runway.depleted}
                            flag={runway.flag}
                        />
                    </div>
                    <p className="mt-2 text-[13px] text-slate-500">{runway.basis}</p>
                    <button
                        type="button"
                        onClick={onShowProjections}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold text-blue-700 hover:border-blue-300 hover:text-blue-900"
                    >
                        <Eye size={15} /> See the projections, year by year
                    </button>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={onBack} className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-[14px] font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-900">
                    <ChevronLeft size={16} /> Change the village costs
                </button>
                <button
                    type="button"
                    onClick={onNext}
                    disabled={!ready}
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Show me ways to fund the move <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default RvStayPutHero;
